import Immutable from 'immutable';
import {combineReducers, createStore, applyMiddleware} from 'redux';
import {connect as reduxConnect} from 'react-redux';
import createLogger from 'redux-logger';
import thunk from 'redux-thunk';
import {createTracker, GATracker} from 'tracking';
import {reducer as configReducer} from './config/reducer';
import {reducer as uiReducer} from './ui/reducer';
import {reducer as videoReducer} from './video/reducer';
import {reducer as elementListReducer} from './element-list/reducer';
import {reducer as captionsReducer} from './captions/reducer';
import {reducer as searchReducer} from './search/reducer';
import {ActionTypes as VideoActionTypes} from './video/actions';

export const configureStore = (adapter, {initialState = {}, debug = false} = {}) => {
    const middleware = [thunk.withExtraArgument(adapter)];

    if (debug) {
        middleware.push(createLogger({
            level: 'info',
            collapsed: true,
            // Do not log UPDATE_PLAYHEAD action because it is dispatched several times per second
            predicate: (getState, action) => action.type !== VideoActionTypes.SET_PLAYHEAD,
            // Transform ImmutableJS to plain objects for easier debugging in the console
            stateTransformer: (state) => {
                const result = {...state};
                Object.keys(state)
                    .filter(key => Immutable.Iterable.isIterable(result[key]))
                    .forEach(key => result[key] = result[key].toJS());
                return result;
            },
        }));
    }

    const tracker = createTracker(new GATracker(VWRAP.trackingAccount, debug));
    middleware.push(tracker);

    const rootReducer = combineReducers({
        config: configReducer,
        ui: uiReducer,
        video: videoReducer,
        search: searchReducer,
        elementList: elementListReducer,
        captions: captionsReducer,
    });

    const store = createStore(
        rootReducer,
        initialState,
        applyMiddleware(...middleware),
    );

    // Helper function that returns a promise which resolves when the store passes a specific check
    store.wait = (check) => new Promise(resolve => {
        const callback = () => {
            if (check(store.getState())) {
                unsubscribe();
                resolve();
            }
        };
        const unsubscribe = store.subscribe(callback);
        callback();
    });

    return store;
};

// Wrapper for redux connect to always map language
// so that all connected components update when the language is changed.
export const connect = (mapStateToProps, mapDispatchToProps) => {
    const wrappedMapStateToProps = (state, ...rest) => {
        const result = mapStateToProps ? mapStateToProps(state, ...rest) : {};
        result.language = state.config.get('language');
        return result;
    };
    return reduxConnect(wrappedMapStateToProps, mapDispatchToProps);
};



// WEBPACK FOOTER //
// ./scripts/store/index.js